import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { useQuery, useMutation } from '@apollo/client';
import {
  Button,
  CircularProgress,
  Stack,
  Typography,
  styled,
} from '@mui/material';
import { theme } from '~/shared/styles/theme';
import { AUTH_STATUS } from '~/graphql/queries';
import { PROVISION_USER } from '~/graphql/mutations';
import { ROUTES } from '~/shared/constants';
import { handleLogout } from '~/shared/utils/auth';

/**
 * SponsorAuthCallback component handles the redirect after Auth0 authentication for sponsors.
 * It queries authStatus to check if the user has sponsor permissions:
 * - If isSponsor: true → upserts User record via provisionUser (backend syncs isSponsor),
 *   then navigates to SPONSOR_DASHBOARD
 * - If isSponsor: false → shows access denied state with logout button
 * - If not authenticated → redirects to login
 */
export const SponsorAuthCallback = () => {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: isAuth0Loading, logout } = useAuth0();
  const hasProvisionedRef = useRef(false);

  const {
    data: authStatusData,
    loading: isAuthStatusLoading,
    error: authStatusError,
  } = useQuery(AUTH_STATUS, {
    skip: !isAuthenticated,
    fetchPolicy: 'network-only',
  });

  const [provisionUser, { loading: isProvisioning }] = useMutation(
    PROVISION_USER,
    {
      refetchQueries: [{ query: AUTH_STATUS }],
      awaitRefetchQueries: true,
    },
  );

  const isSponsor = authStatusData?.authStatus?.isSponsor;

  useEffect(() => {
    if (isAuth0Loading || isAuthStatusLoading) {
      return;
    }

    if (!isAuthenticated || authStatusError) {
      navigate(ROUTES.LOGIN);
    }
  }, [
    isAuthenticated,
    isAuth0Loading,
    isAuthStatusLoading,
    authStatusError,
    navigate,
  ]);

  useEffect(() => {
    if (!isSponsor || hasProvisionedRef.current || isProvisioning) {
      return;
    }

    hasProvisionedRef.current = true;

    const provision = async () => {
      try {
        await provisionUser();
      } finally {
        navigate(ROUTES.SPONSOR_DASHBOARD);
      }
    };

    provision();
  }, [isSponsor, isProvisioning, provisionUser, navigate]);

  // Loading state
  if (isAuth0Loading || isAuthStatusLoading || isProvisioning) {
    return (
      <LoadingContainer
        direction="row"
        role="status"
        aria-busy="true"
        aria-label="Signing in"
      >
        <LoadingContent>
          <CircularProgress size={48} aria-hidden="true" />
        </LoadingContent>
      </LoadingContainer>
    );
  }

  // Access denied state
  if (isAuthenticated && authStatusData?.authStatus?.isSponsor === false) {
    return (
      <AccessDeniedContainer>
        <AccessDeniedContent>
          <Typography variant="h4" component="h1" gutterBottom>
            Access Denied
          </Typography>
          <Typography variant="body1" color="text.secondary" gutterBottom>
            You do not have permission to access the sponsor dashboard.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
            Please contact support if you believe this is an error.
          </Typography>
          <Button
            variant="contained"
            onClick={() => handleLogout(logout)}
            sx={{
              backgroundColor: theme.palette.custom.green,
              color: theme.palette.common.white,
              '&:hover': {
                backgroundColor: theme.palette.custom.green,
                opacity: 0.9,
              },
            }}
          >
            Logout
          </Button>
        </AccessDeniedContent>
      </AccessDeniedContainer>
    );
  }

  // Fallback loading state (shouldn't reach here, but just in case)
  return (
    <LoadingContainer
      direction="row"
      role="status"
      aria-busy="true"
      aria-label="Signing in"
    >
      <LoadingContent>
        <CircularProgress size={48} aria-hidden="true" />
      </LoadingContent>
    </LoadingContainer>
  );
};

const LoadingContainer = styled(Stack)({
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: '100vh',
  backgroundColor: theme.palette.custom.cream,
});

const LoadingContent = styled(Stack)({
  alignItems: 'center',
  gap: theme.spacing(2),
});

const AccessDeniedContainer = styled(Stack)({
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: '100vh',
  backgroundColor: theme.palette.custom.cream,
});

const AccessDeniedContent = styled(Stack)({
  alignItems: 'center',
  textAlign: 'center',
  maxWidth: 400,
  padding: theme.spacing(4),
});
